import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import setAuthHeader from '../../helpers/set-auth-token';

const API_ENDPOINT = '/login';

export const SESSION_IMPERSONATION_KEY = 'isImpersonationMode';

// Create the authentication slice
const authSlice = createSlice({
  initialState: {
    isAuthSet: false,
    token: null,
    loading: false,
    isImpersonationMode: localStorage.getItem(SESSION_IMPERSONATION_KEY) === 'true' || false,
  },
  name: 'authSlice',
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload;
      state.isAuthSet = true;
    },
    setImpersonationMode: (state, action) => {
      state.isImpersonationMode = action.payload;
      localStorage.setItem(SESSION_IMPERSONATION_KEY, action.payload);
    },
    logOut: state => {
      state.token = null;
      state.isImpersonationMode = false;
      state.isAuthSet = true;
      Sentry.setUser(null);
    },
  },
  extraReducers: builder => {
    // Handle pending login request
    builder
      .addCase(login.pending, state => {
        state.loading = true;
      })
      // Handle successful login response
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.isImpersonationMode = false;
        localStorage.removeItem(SESSION_IMPERSONATION_KEY);
      })
      // Handle login error
      .addCase(login.rejected, (state, { error }) => {
        state.loading = false;
        toast.error(error?.message || 'Something went wrong');
      })
      .addCase(enterImpersonationMode.pending, state => {
        state.loading = true;
      })
      .addCase(enterImpersonationMode.fulfilled, (state, action) => {
        state.loading = false;
        state.isImpersonationMode = true;
        state.token = action.payload.token;
        localStorage.setItem(SESSION_IMPERSONATION_KEY, true);
      })
      .addCase(enterImpersonationMode.rejected, (state, { error }) => {
        state.loading = false;
        state.isImpersonationMode = false;
        localStorage.removeItem(SESSION_IMPERSONATION_KEY);
        toast.error(error?.message || 'Something went wrong');
      })
      .addCase(exitImpersonationMode.pending, state => {
        state.loading = true;
      })
      .addCase(exitImpersonationMode.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isImpersonationMode = false;
        state.token = payload.token;
        localStorage.removeItem(SESSION_IMPERSONATION_KEY);
      })
      .addCase(exitImpersonationMode.rejected, (state, { error }) => {
        state.loading = false;
        state.isImpersonationMode = false;
        state.token = null;
        setAuthHeader();
        localStorage.removeItem(SESSION_IMPERSONATION_KEY);
        toast.error(error?.message || 'Something went wrong');
      });
  },
});

// Export the authentication reducer
export default authSlice.reducer;

// Export the setToken action
export const { setToken, setImpersonationMode, logOut } = authSlice.actions;

// Create an async thunk for the login action
export const login = createAsyncThunk('authSlice/login', async payload => {
  try {
    const response = await axios.post(API_ENDPOINT, payload);
    const token = response?.data?.token;

    // Set the authentication token in the request header
    setAuthHeader(token, payload?.rememberMe !== false);

    return { token };
  } catch (err) {
    throw err?.response?.data;
  }
});

// Stored whitelabel domains are production hostnames — including custom apexes —
// and pre-prod runs on the production database, so hopping from there lands the
// session on production (#4208). Pre-prod, preview channels and localhost have no
// tenant subdomains of their own and never hop; dev hops only to another dev host.
const NEVER_HOP_HOSTS = [/^localhost$/, /^127\./, /^\[?::1\]?$/, /\.web\.app$/, /^app-preprod\./];
const DEV_HOST = /\.ribeye\.tools$/;

// Hostnames are case-insensitive and may carry a root-label trailing dot.
const normalizeHost = value => (value || '').toLowerCase().replace(/\.$/, '');

const canHopTo = (rawHost, rawDomain) => {
  const host = normalizeHost(rawHost);
  const domain = normalizeHost(rawDomain);
  if (NEVER_HOP_HOSTS.some(pattern => pattern.test(host))) return false;
  return DEV_HOST.test(host) ? DEV_HOST.test(domain) : true;
};

// Absolute-URL hostname, or null if unparseable — never throws on a malformed value.
const safeHostOf = value => {
  try {
    return new URL(value).hostname;
  } catch {
    return null;
  }
};

// Absolute-URL scheme (e.g. "https:"), or null if unparseable.
const safeSchemeOf = value => {
  try {
    return new URL(value).protocol;
  } catch {
    return null;
  }
};

// The fragment (after #) is never sent in the request line, so it doesn't land in
// Cloud Run / CDN access logs the way a query string does (flank#5116, AUTH-04).
// Built via the URL API, not string concatenation: `origin` here can be the
// attacker-controlled `redirectedFrom` value (once it's passed AUTH-03's host
// and scheme check), which may already carry its own `#fragment` — appending
// a second `#` would produce a malformed, unparsable URL (review, CodeRabbit,
// flank#5117).
const buildHopUrl = (origin, params) => {
  const url = new URL(origin);
  url.hash = new URLSearchParams(params).toString();
  return url.toString();
};

export const enterImpersonationMode = createAsyncThunk('meSlice/enterImpersonationMode', async ({ userId }) => {
  try {
    const { data } = await axios.post(`/impersonate/${userId}`);
    const {
      token: { token },
      domain,
    } = data;

    if (domain && canHopTo(window.location.hostname, domain)) {
      const redirectUrl = buildHopUrl(`${window.location.protocol}//${domain}`, {
        token,
        impersonationMode: 'true',
        redirectedFrom: window.location.href,
      });
      window.location.href = redirectUrl;
      return;
    }

    setAuthHeader(token);

    return { token };
  } catch (err) {
    throw err?.response?.data;
  }
});

export const exitImpersonationMode = createAsyncThunk('meSlice/exitImpersonationMode', async () => {
  try {
    const { data } = await axios.post('/impersonate/exit');
    const { token, originHost } = data;

    const redirectedFrom = localStorage.getItem('redirectedFrom');
    localStorage.removeItem('redirectedFrom');

    // originHost is attested server-side from the admin's own Origin/Referer header at
    // impersonate-enter time. redirectedFrom alone is attacker-controlled (settable via a
    // crafted impersonation link) and can't be trusted as a redirect destination on its
    // own, so it's only used verbatim when both its host AND scheme match the
    // server-trusted host (flank#5116, AUTH-03 — matching hostname alone still let an
    // https origin be replayed over a plain-http redirectedFrom carrying the same host).
    // Already on originHost: no hop needed at all, just set the token in place.
    if (originHost && originHost !== window.location.hostname) {
      const target =
        redirectedFrom &&
        safeHostOf(redirectedFrom) === originHost &&
        safeSchemeOf(redirectedFrom) === window.location.protocol
          ? redirectedFrom
          : `${window.location.protocol}//${originHost}`;
      window.location.href = buildHopUrl(target, { token });
      return;
    }

    setAuthHeader(token);

    return { token };
  } catch (err) {
    throw err?.response?.data;
  }
});
